"use client";

type ChatEventType =
  | "new-chat"
  | "delete-chat"
  | "update-chat"
  | "update-title"
  | "update-icon"
  | "stream-start";

/**
 * Data passed with chat lifecycle events
 * Allows optimistic UI updates without extra fetches
 */
interface ChatEventData {
  title?: string;
  /** kebab-case lucide icon name (e.g. "message-circle"). Streamed alongside title generation. */
  icon?: string;
  agentId?: string | null;
  teamId?: string | null;
}

type ChatEventCallback = (chatId: string, data?: ChatEventData) => void;

class ChatEventEmitter {
  private listeners: Record<ChatEventType, ChatEventCallback[]> = {
    "new-chat": [],
    "delete-chat": [],
    "update-chat": [],
    "update-title": [],
    "update-icon": [],
    "stream-start": [],
  };

  public subscribe(
    event: ChatEventType,
    callback: ChatEventCallback
  ): () => void {
    this.listeners[event].push(callback);

    // Return unsubscribe function
    return () => {
      this.listeners[event] = this.listeners[event].filter(
        (cb) => cb !== callback
      );
    };
  }

  public emit(event: ChatEventType, chatId: string, data?: ChatEventData): void {
    this.listeners[event].forEach((callback) => callback(chatId, data));
  }
}

// Create a singleton instance
const chatEventEmitter = new ChatEventEmitter();

export function useChatEvents() {
  return {
    /**
     * Subscribe to chat events
     * @param event The event type to subscribe to
     * @param callback The callback to execute when the event is emitted
     * @returns A function to unsubscribe from the event
     */
    subscribe: chatEventEmitter.subscribe.bind(chatEventEmitter),

    /**
     * Emit a chat event
     * @param event The event type to emit
     * @param chatId The ID of the chat related to the event
     * @param data Optional event payload for optimistic UI state
     */
    emit: chatEventEmitter.emit.bind(chatEventEmitter),
  };
}
